






























































































































































































































import Vue from 'vue'
import { minLength, required } from 'vuelidate/lib/validators'
import { Option, Select } from 'element-ui'
import SelfTest from '@/components/SelfTest'
import SelfTestSummary from '@/components/SelfTestSummary'
import EditUserEncoderSettings from '@/components/EditUserEncoderSettings'
import ImageUploader from '@/components/ImageUploader'
import PreviewVideoQualityToggle from '@/components/PreviewVideoQualityToggle'
import helpers from '../../helpers'
import { isEqual, sortBy } from 'lodash'
import { UserProfile } from '@/types/users'
import { TranscodingRegion } from '@/types/admin'
import roleNames from '@/roleNames'
import { isMobile } from 'mobile-device-detect'
import { AbilityAction, AbilitySubject, TeamRole } from '@/abilitiesByRole'
import SubscribeTeamRoles from '@/mixins/SubscribeTeamRoles'
import '@/styles/components/forms.scss'
import './UserProfileForm.scss'
import { KeyDict } from '@/types'
import { TeamGroup } from '@/types/teams'
import { Feature } from '@/featureFlags/featureFlags'

interface RegionOption {
  name: string,
  value: null | string
}

export default Vue.extend({
  name: 'UserProfileForm',
  props: {
    value: {
      type: Object as () => UserProfile
    },
    transcodingregions: {
      type: Object as () => { [id: string ]: TranscodingRegion }
    },
    short: {
      type: Boolean,
      default: false
    },
    anonymous: {
      type: Boolean,
      default: false
    },
    selfTest: {
      type: Boolean,
      default: false
    }
  },
  mixins: [ SubscribeTeamRoles ],
  created () {
    this.originalProfile = JSON.parse(JSON.stringify(this.value))
    this.editProfile = JSON.parse(JSON.stringify(this.value))
    if (!this.editProfile.teamGroups?.hasOwnProperty(this.currentTeam)) {
      if (!this.editProfile.teamGroups) this.editProfile.teamGroups = {}
      this.editProfile.teamGroups[this.currentTeam] = []
    }
  },
  computed: {
    isMobile (): boolean {
      return isMobile
    },
    isSelfServe (): boolean {
      return this.$hasFeature(Feature.SELF_SERVE)
    },
    canEditMembers (): boolean {
      return this.$can(AbilityAction.Edit, AbilitySubject.Members)
    },
    canEditTeamSettings (): boolean {
      return this.$can(AbilityAction.Edit, AbilitySubject.TeamSettings)
    },
    canEditCasterStreams (): boolean {
      return this.$can(AbilityAction.Edit, AbilitySubject.CasterStream)
    },
    canDoOps (): boolean {
      return this.$can(AbilityAction.Do, AbilitySubject.Ops)
    },
    teamRoles (): KeyDict<TeamRole> {
      return this.$store.direct.state.abilities.teamRoles
    },
    teamGroups (): TeamGroup[] {
      return this.$store.direct.state.team.teamGroups ?? []
    },
    sortedTeamGroups (): TeamGroup[] {
      return sortBy(this.teamGroups, group => group.name.toLowerCase())
    },
    currentUserId (): string {
      return this.$store.direct.state.user.id
    },
    currentTeam (): string {
      return this.$store.direct.state.team.activeTeam
    },
    canEditCast (): boolean {
      return this.$can(AbilityAction.Edit, AbilitySubject.Cast)
    },
    userIsMe (): boolean {
      return !!this.originalProfile && this.originalProfile.id === this.currentUserId
    },
    warningName (): string {
      const profileName = (this.originalProfile) ? this.originalProfile.first_name : 'User'
      return (this.userIsMe) ? 'You' : profileName
    },
    showWarning (): boolean {
      return this.regionChanged || this.roleChanged || this.encoderSettingsChanged || this.echoSuppressionChanged || this.udpChanged
    },
    regionChanged (): boolean {
      return this.originalProfile?.transcodingregion !== this.editProfile.transcodingregion
    },
    roleChanged (): boolean {
      return this.originalProfile?.role !== this.editProfile.role
    },
    echoSuppressionChanged (): boolean {
      let status = false
      if (this.originalProfile?.echoSuppression !== undefined && this.editProfile?.echoSuppression !== undefined) {
        status = this.originalProfile.echoSuppression !== this.editProfile.echoSuppression
      }
      return status
    },
    udpChanged (): boolean {
      let status = false
      if (this.originalProfile?.udpEnabled !== undefined && this.editProfile?.udpEnabled !== undefined) {
        status = this.originalProfile.udpEnabled !== this.editProfile.udpEnabled
      }
      return status
    },
    encoderSettingsChanged (): boolean {
      const original = this.originalProfile?.encoderSettings !== undefined ? JSON.parse(JSON.stringify(this.originalProfile.encoderSettings)) : {}
      const editProfile = this.editProfile.encoderSettings !== undefined ? JSON.parse(JSON.stringify(this.editProfile.encoderSettings)) : {}
      const defaultValToCheckFor = { width: 640, height: 360 }
      if ((!this.originalProfile?.encoderSettings || Object.keys(this.originalProfile?.encoderSettings).length == 0) && isEqual(editProfile, defaultValToCheckFor))
        return false
      return !isEqual(original, editProfile)
    },
    needsToReconnect () : boolean{
      const r = this.encoderSettingsChanged || this.echoSuppressionChanged || this.udpChanged
      return r
    },
    roleNamesOptions (): KeyDict<string> {
      return roleNames()
    },
    possibleRegions (): RegionOption[] {
      const regions = [] as RegionOption[]
      for (const key in this.transcodingregions) {
        regions.push({ name: this.transcodingregions[key].name, value: key })
      }
      return sortBy(regions, 'name')
    },
    isMac (): boolean {
      return helpers.isMac()
    }
  },
  data: () => ({
    editProfile: {} as UserProfile,
    originalProfile: {} as UserProfile,
    runSelfTest: false,
    formInvalid: false,
    showTroubleShooter: false
  }),
  watch: {
    originalProfile: {
      handler (newVal: UserProfile) {
        console.log('originalProfile changed outside ', newVal)
      },
      deep: true
    },
    editProfile: {
      handler (newVal: UserProfile) {
        if (!isEqual(newVal, this.value)) {
          this.$emit('input', newVal)
        }

        const settingsComponent = this.$refs.settingsElement as any
        const encoderSettingsInvalid = settingsComponent?.$v.$invalid
        // console.log('settings valid ', !encoderSettingsInvalid)

        this.$v.$touch()
        this.$emit('formvalid', !this.$v.$invalid && !encoderSettingsInvalid)
      },
      deep: true
    },
    needsToReconnect (newVal: boolean) {
      this.$emit('setNeedsRepublishing', { user_id: this.editProfile.id, val: newVal})
    },
    value (newVal: UserProfile, oldVal: UserProfile) {
      if (isEqual(this.editProfile.selftest, newVal.selftest)) {
        this.editProfile = JSON.parse(JSON.stringify(newVal))
      } else {
        this.editProfile = JSON.parse(JSON.stringify(oldVal))
        this.editProfile.selftest = JSON.parse(JSON.stringify(newVal.selftest))
      }
    },
    runSelfTest (newVal) {
      const scrollWindow = document.getElementsByClassName('modal-body')
      if (scrollWindow.length > 0) {
        setTimeout(() => {
          if (newVal) {
            scrollWindow[0].scrollTo({
              top: 1000,
              left: 0,
              behavior: 'smooth'
            })
          } else {
            scrollWindow[0].scrollTo({
              top: 0,
              left: 0,
              behavior: 'smooth'
            })
          }
        }, 100)
      }
    }
  },
  methods: {
    openSelfTest () {
      if (this.isMobile) {
        const route = this.$router.resolve({ name: 'selftest' })
        window.location.href = route.href
        return
      }

      this.runSelfTest = !this.runSelfTest
      this.showTroubleShooter = false
    },
    toggleTroubleShooter () {
      this.showTroubleShooter = !this.showTroubleShooter
      this.runSelfTest = false
    },
    ontoggle (val) {
      this.$set(this.editProfile, 'previewVideoQuality', val.previewVideoQualityValue)
      this.$set(this.editProfile, 'chromaKeyPreviewQuality', val.chromaKeyPreviewQualityValue)
    }
  },
  validations: {
    editProfile: {
      first_name: {
        required,
        minLength: minLength(2)
      },
      last_name: {
        required,
        minLength: minLength(2)
      },
      role: {
        required
      }
    }
  },
  components: {
    'el-select': Select,
    'el-option': Option,
    SelfTest,
    SelfTestSummary,
    ImageUploader,
    PreviewVideoQualityToggle,
    EditUserEncoderSettings
  }
})
